import './ManageOrdersTable.scss';

import { Multiselect, MultiselectProps } from '@amzn/awsui-components-react/polaris';
import {
  Box,
  CollectionPreferences,
  Container,
  Grid,
  Header,
  PropertyFilter,
  Pagination,
  PaginationProps,
  Spinner,
  Table,
  TableProps
} from '@amzn/awsui-components-react';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import Select, { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { Order, OrderItem } from '@amzn/ito-client';
import { ColumnSetting, VisibleOption } from 'common/utils/config-models';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import EmptyTableBox from '../../common/EmptyTableBox/EmptyTableBox';
import { getPropertyFilterString, getString } from '../../common/ui-string-labels/ui-strings-utils';
import {
  SortingState,
  buildActiveContentDisplay,
  buildContentDisplayOptions,
  buildFilteringProperties,
  buildMultiSelectLocationOptions,
  buildOrderedColumnDefinitions,
  buildSelectOptionsFromStrings,
  convertTokensToPropertyFilter,
  defaultPagingOptions
} from '../../common/utils';
import { useGetOrders } from '../../hooks/orders';

import './ManageOrdersTable.scss';
import ordersTableConfig from './orders.table-config.json';
import { useManagerOrdersTableResult } from './useManageOrdersTable';

import { useFlashBarItemsActions } from 'common/UseFlashBarItems/useFlashBarItems';
import { usePreferences } from 'hooks/usePreferences';
import { CurrentPage } from 'common/utils/config-models';
import { ManageOrdersTableHeader } from './ManageOrdersTableHeader';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { UserPermissions } from 'services/user-permissions';
import { MANAGE_ORDER_TABS, STATUS_OPTIONS } from 'interfaces';
import { PERMISSIONS } from 'configuration/config';
import { DownloadLoading } from "../../common/DownloadLoading/DownloadLoading";

const uploadedOrdersVisibleColumns = [
  'orderId',
  'ageDays',
  'requestedBy'
];

const PREFERENCES_KEY = 'ManageOrdersPreferences';
const columns: ColumnSetting[] = ordersTableConfig.columns;
const FILTERING_PROPERTIES = buildFilteringProperties(columns);
const LOCATION_OPTIONS = buildMultiSelectLocationOptions();

// Select the Open Status options
const openStatusOptions = STATUS_OPTIONS.find(group => group.label === "Open Status")?.options || [];

const EXPEDITED_OPTIONS = [
  { label: 'Expedited', value: '' },
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

export interface ManageOrdersTableProps {
  updateOrderSelected?: (showSplitPanel: boolean, orderSelected: string, orderItems: OrderItem[] | undefined) => void;
  useFlashBarItemsActions?: useFlashBarItemsActions;
  useManageOrdersTableHook?: useManagerOrdersTableResult;
  fileId?: string;
  fileName?: string;
}

export const ManageOrdersTable: FunctionComponent<ManageOrdersTableProps> = (props: ManageOrdersTableProps) => {
  // Actions
  const { useFlashBarItemsActions, useManageOrdersTableHook } = props;
  const { attributes, actions } = useManageOrdersTableHook!;
  const [loading,] = useState(false);

  const [currentSorting, setCurrentSorting] = useState<SortingState | undefined>(undefined);

  const url = new URL(window.location.href);

  // Show and hides the split panel based on items selected
  /* istanbul ignore next */
  useEffect(() => {
    if (attributes.selectedItems.length === 0) {
      props.updateOrderSelected!(false, "", []);
    } else if (attributes.selectedItems.length >= 1 && attributes.selectedItems[attributes.selectedItems.length - 1]) {
      const orderSelected = attributes.selectedItems[attributes.selectedItems.length - 1]?.orderReference ?? "";
      const orderItems = attributes.selectedItems[attributes.selectedItems.length - 1]?.items;
      props.updateOrderSelected!(true, orderSelected, orderItems);
    }
  }, [attributes.selectedItems]);

  // Preferences
  const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
    preferencesKey: PREFERENCES_KEY,
    refreshData: () => refreshData(true),
    columnsSettings: columns,
    defaultPaging: defaultPagingOptions[1].value
  });

  const [currentPageIndex, setCurrentPageIndex] = useState<CurrentPage>({ index: 1, isPageReset: true });
  const [selectedStatus, setSelectedStatus] = useState<OptionDefinition[]>(openStatusOptions);
  const [selectedExpedited, setSelectedExpedited] = useState(EXPEDITED_OPTIONS[0]);
  const [selectedOrderTypeTab, setSelectedOrderTypeTab] = useState("");

  const ASSIGNED_TO_OPTIONS: SelectProps.Option[] = [
    { label: 'unassigned', value: '-' },
    ...buildSelectOptionsFromStrings(attributes.procurementMembersSystem)
  ];

  const currentUsername = UserPermissions.instance.username;

  const [selectedAssignee, setSelectedAssignee] = useState<OptionDefinition[]>([
    { label: currentUsername, value: currentUsername }
  ]);
  const [selectedLocations, setSelectedLocations] = useState<OptionDefinition[]>([]);

  // To get orders from ITO
  const { ordersData, isLoading, doGetOrders } = useGetOrders(
    { pageSize: currentPreferences.pageSize, pageIndex: 1 },
    undefined
  );

  useEffect(() => {
    refreshData(true);
  }, [selectedAssignee, selectedStatus, selectedLocations, selectedExpedited, attributes.currentFilteringQuery, currentSorting?.sortBy, currentSorting?.sortOrder]);

  useEffect(() => {
    if (!currentPageIndex.isPageReset) {
      refreshData(false);
    }
  }, [currentPageIndex.index, currentPageIndex.isPageReset]);

  /* istanbul ignore next */
  const onStatusChange: NonCancelableEventHandler<MultiselectProps.MultiselectChangeDetail> = e => {
    setSelectedStatus([...e.detail.selectedOptions]);
  };

  /* istanbul ignore next */
  const onExpeditedChange: NonCancelableEventHandler<SelectProps.ChangeDetail> = e => {
    setSelectedExpedited({
      label: e.detail.selectedOption.label!,
      value: e.detail.selectedOption.value!
    });
  };

  /* istanbul ignore next */
  const onAssignedChange: NonCancelableEventHandler<MultiselectProps.MultiselectChangeDetail> = e => {
    setSelectedAssignee([...e.detail.selectedOptions]);
  };

  const onLocationChange: NonCancelableEventHandler<MultiselectProps.MultiselectChangeDetail> = e => {
    setSelectedLocations([...e.detail.selectedOptions]);
  };

  // Sorting
  /* istanbul ignore next */
  const onSortingChange: NonCancelableEventHandler<TableProps.SortingState<Order>> = async e => {
    const filterField = e.detail.sortingColumn.sortingField;
    const sortFieldMapping: Record<string, string> = {
      ageDays: "requested.on",
      onBehalf: "requester.userAlias",
    };
    const sortBy = sortFieldMapping[filterField!] ?? filterField;
    const sortOrder = e.detail.isDescending ? 'sort.desc' : 'sort.asc';
    setCurrentSorting({ sortBy, sortOrder });
    collectionProps.onSortingChange!(e);
  };

  const { collectionProps } = useCollection(ordersData?.orders ?? [], {
    sorting: {}
  });

  // Pagination
  /* istanbul ignore next */
  const onPageClick: NonCancelableEventHandler<PaginationProps.ChangeDetail> = e => {
    setCurrentPageIndex({ index: e.detail.currentPageIndex, isPageReset: false });
  };

  useEffect(() => {
    refreshData(true);
  }, [selectedOrderTypeTab])

  /* istanbul ignore next */
  const refreshData = (clearPreviousData: boolean) => {
    let pageIndexForQuery = currentPageIndex.index;
    if (clearPreviousData) {
      pageIndexForQuery = 1;
      setCurrentPageIndex({ index: 1, isPageReset: true });
    }
    const filters: { [key: string]: string[] } = convertTokensToPropertyFilter(attributes.currentFilteringQuery.tokens);

    if (selectedOrderTypeTab && selectedOrderTypeTab === MANAGE_ORDER_TABS.IT_SHOP_ORDERS_SUPPORT_REQUESTED) {
      filters["orderSupport.supportRequested"] = ["true"];
    }
    if (selectedOrderTypeTab && selectedOrderTypeTab != MANAGE_ORDER_TABS.IT_SHOP_ORDERS && selectedOrderTypeTab != MANAGE_ORDER_TABS.IT_SHOP_ORDERS_SUPPORT_REQUESTED) {
      filters["orderLabels.label"] = [selectedOrderTypeTab];
    }
    if (selectedStatus.length > 0) {
      filters.status = selectedStatus.map(item => item.value as string);
    }

    // merge PropertyFilter search with multiselect filter
    if (selectedAssignee.length > 0) {
      const multiselectAssignees = selectedAssignee.map(item => item.value as string);
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        // Merge both PropertyFilter search and multiselect filter values
        filters.assignedTo = [...new Set([...filters.assignedTo, ...multiselectAssignees])];
      } else {
        // Only multiselect filter values
        filters.assignedTo = multiselectAssignees;
      }
    }

    if (selectedLocations.length > 0) {
      filters['details.deliverTo.country'] = selectedLocations.map(item => item.value as string);
    }
    if (selectedExpedited.value) {
      filters['details.isExpedite'] = [selectedExpedited.value];
    }
    if (currentSorting && currentSorting.sortOrder && currentSorting.sortBy) {
      filters[currentSorting.sortOrder] = [currentSorting.sortBy];
    }

    actions.refreshAttributes();
    doGetOrders({
      filters,
      isAmznHam: selectedOrderTypeTab === MANAGE_ORDER_TABS.IT_SHOP_ORDERS || selectedOrderTypeTab === MANAGE_ORDER_TABS.IT_SHOP_ORDERS_SUPPORT_REQUESTED,
      pageSize: currentPreferences.pageSize!,
      pageIndex: pageIndexForQuery,
    });
  };

  // Ready to display
  let pagesCount = 0;
  let totalResults = 0;
  if (ordersData && ordersData.totalResults && ordersData.totalResults > 0) {
    totalResults = ordersData.totalResults;
    pagesCount = Math.ceil(ordersData.totalResults / currentPreferences.pageSize!);
  }

  const uploadErrorColumnIndex = columns.findIndex(c => c.id === 'uploadError');
  /* istanbul ignore next */
  if (uploadErrorColumnIndex >= 0) {
    columns[uploadErrorColumnIndex].visible = props.fileId ? VisibleOption.yes : VisibleOption.never;
  }

  /* istanbul ignore next */
  const effectiveContentDisplay = (props.fileId && uploadErrorColumnIndex >= 0)
    ? uploadedOrdersVisibleColumns.map(id => ({ id, visible: true }))
    : currentPreferences.contentDisplay;

  // Feature flags
  const showItemsColumn = useMemo(() => UserPermissions.instance.hasPermission(PERMISSIONS.SHOW_ITEMS_LIST_COLUMN), []);
  const showRelatedOrders = useMemo(() => UserPermissions.instance.hasPermission(PERMISSIONS.SHOW_RELATED_ORDERS), []);

  /**
 * Filters and transforms the columns array based on conditions:
 * 1. Removes support-related columns if not on support requested tab
 * 2. Removes requested items column if user lacks permission
 * 3. Renames legacy order ID column to "Related orders" if user has permission
 *
 * @param columns - Array of column settings to filter/transform
 * @param selectedOrderTypeTab - Currently selected order type tab
 * @param showItemsColumn - Whether user has permission to see items column
 * @param showRelatedOrders - Whether user has permission to see related orders
 *
 * @returns Filtered and transformed columns array
 */
  const filteredColumns = useMemo(() => {
    return columns.filter(column => {
      if ((column.id === "supportMessages" || column.id === "orderSupport.lastCommentInfo.on") && selectedOrderTypeTab !== MANAGE_ORDER_TABS.IT_SHOP_ORDERS_SUPPORT_REQUESTED) {
        return false;
      }
      if (column.id === "requestedItems" && !showItemsColumn) {
        return false;
      }
      return true;
    }).map(column => {
      if (column.id === "details.legacyOrderId" && showRelatedOrders) {
        return {
          ...column,
          header: "Related orders"
        };
      }
      return column;
    });
  }, [columns, selectedOrderTypeTab, showItemsColumn, showRelatedOrders]);

  const CONTENT_DISPLAY_OPTIONS = buildContentDisplayOptions(filteredColumns);

  const activeContentDisplay = buildActiveContentDisplay(filteredColumns, effectiveContentDisplay);

  const activePreferences = {
    ...currentPreferences,
    contentDisplay: activeContentDisplay
  };

  const currentColumnDefinitions = buildOrderedColumnDefinitions(filteredColumns, activeContentDisplay);

  if (loading) {
    return (
        <DownloadLoading/>
    );
  }

  return (
    <Box data-testid="manage-orders-box">
      <Table
        {...collectionProps}
        data-testid="manage-orders-table"
        loading={isLoading}
        onSelectionChange={actions.onSelectionChangeHandler}
        selectedItems={attributes.selectedItems}
        header={
          <ManageOrdersTableHeader
            totalResults={totalResults}
            selectedItems={attributes.selectedItems!}
            refreshData={refreshData}
            useFlashBarItemsActions={useFlashBarItemsActions!}
            onOrderTypeTabChange={setSelectedOrderTypeTab}
            selectedOrderTypeTab={selectedOrderTypeTab}
            procurementMembersSelectOptions={buildSelectOptionsFromStrings(attributes.procurementMembers)}
          />
        }
        filter={
          <Grid gridDefinition={[{ colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }]}>
            <PropertyFilter
              expandToViewport
              query={attributes.currentFilteringQuery}
              onChange={actions.onPropertyFilterChange}
              filteringProperties={FILTERING_PROPERTIES}
              i18nStrings={getPropertyFilterString()}
              filteringPlaceholder={getString("manageOrders.table.textFilter")}
              hideOperations={true}
              data-testid="orders-text-filter"
            />
            <Select
              data-testid="select-expedited"
              selectedOption={selectedExpedited}
              onChange={onExpeditedChange}
              options={EXPEDITED_OPTIONS}
              selectedAriaLabel="Selected"
            />
            <Multiselect
              data-testid="select-status"
              placeholder="Status"
              filteringType="auto"
              selectedOptions={selectedStatus}
              onChange={onStatusChange}
              options={STATUS_OPTIONS}
              selectedAriaLabel="Selected"
              tokenLimit={2}
            />
            <Multiselect
              data-testid="select-assigned"
              placeholder="Assigned To"
              filteringType="auto"
              selectedOptions={selectedAssignee}
              onChange={onAssignedChange}
              options={ASSIGNED_TO_OPTIONS}
              selectedAriaLabel="Selected"
            />
            <Multiselect
              expandToViewport
              data-testid="select-location"
              placeholder="Region / Country"
              filteringType="auto"
              selectedOptions={selectedLocations}
              onChange={onLocationChange}
              options={LOCATION_OPTIONS}
              selectedAriaLabel="Selected"
              tokenLimit={2}
            />
          </Grid>
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex.index}
            pagesCount={pagesCount}
            onChange={onPageClick}
            ariaLabels={{
              nextPageLabel: getString('common.pagination.nextPage'),
              previousPageLabel: getString('common.pagination.nextPage'),
              pageLabel: (pageNumber: number) => getString('common.pagination.currentPage', undefined, { pageNumber, pagesCount })
            }}
          />
        }
        onSortingChange={onSortingChange}
        empty={<EmptyTableBox />}
        stripedRows={currentPreferences.stripedRows}
        preferences={
          props.fileId ? undefined : (
            <CollectionPreferences
              title={getString('common.preferences.title')}
              confirmLabel={getString('common.preferences.confirmLabel')}
              cancelLabel={getString('common.preferences.cancelLabel')}
              pageSizePreference={{
                title: getString('common.preferences.pageSize.title'),
                options: defaultPagingOptions
              }}
              stripedRowsPreference={{
                label: getString('common.preferences.stripedRows.label'),
                description: getString('common.preferences.stripedRows.description')
              }}
              contentDisplayPreference={{
                title: getString('common.preferences.visibleContentPreference.title'),
                options: CONTENT_DISPLAY_OPTIONS
              }}
              preferences={activePreferences}
              onConfirm={onPreferencesConfirmHandler}
            />
          )
        }
        columnDefinitions={currentColumnDefinitions}
        loadingText={getString('common.loading')}
        items={ordersData?.orders!}
        resizableColumns={true}
        selectionType="multi"
        variant="full-page"
        wrapLines
      />
    </Box>
  );
};
